import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Award, Users, ChevronRight } from "lucide-react";
import { api, getChildId } from "../lib/api";
import { loadRecordings, play, setAudioPrefs } from "../lib/audio";
import { Avatar } from "../components/Avatar";
import { ParentGate } from "../components/ParentGate";

const HALLS = [
  { key: "classic", path: "/play", name: "經典故事館", sub: "孔融讓梨、曹沖稱象、守株待兔……", learn: "學注音・認國字・寫一寫", tone: "bg-[#FFF4E0] border-[#c9a56b]", btn: "bg-[var(--vermilion)]", font: "font-serif-tc", title: "text-[var(--vermilion)]" },
  { key: "life", path: "/life", name: "生活中文館", sub: "我的家、吃飯、上學、去超市……", learn: "學生活詞彙・實用句型・開口說", tone: "bg-[#E9F6F1] border-[#7cc4b4]", btn: "bg-[#3E9C8F]", font: "font-round", title: "text-[#2c6e65]" },
  { key: "speak", path: "/speak", name: "我會說中文館", sub: "我要喝水、我肚子痛、阿嬤我想你……", learn: "聽一聽・選一選・錄下來・開口說", tone: "bg-[#FFF1E6] border-[#F5C9AE]", btn: "bg-[#D9573B]", font: "font-round", title: "text-[#8F3520]" },
  { key: "zhuyin", path: "/zhuyin", name: "注音練習館", sub: "ㄅㄆㄇㄈ……37 個注音卡", learn: "聽發音・念口訣・描一描・聽音選注音", tone: "bg-[#E3F2FB] border-[#b9dcf0]", btn: "bg-[#1D6FB8]", font: "font-serif-tc", title: "text-[#1D6FB8]", done: "學會" },
  { key: "art", path: "/art", name: "小書童畫坊", sub: "塗色、跟著畫、燈籠、十二生肖……", learn: "塗一塗・畫一畫・聽典故・小畫廊", tone: "bg-[#FFF0F3] border-[#f7c6d2]", btn: "bg-[#E2683C]", font: "font-round", title: "text-[#B4471F]" },
];

const HallCard = ({ h, info, onGo, i }) => (
  <motion.button data-testid={`hall-card-${h.key}`} onClick={onGo} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.15 }}
    whileHover={{ y: -8 }} whileTap={{ scale: 0.97 }} className={`text-left rounded-[2.5rem] border-4 overflow-hidden shadow-xl flex flex-col ${h.tone}`}>
    <div className="relative h-52 sm:h-64 overflow-hidden">
      <img src={info?.cover} alt={h.name} className="w-full h-full object-cover" />
      <div className="absolute top-4 left-4 px-4 py-1.5 rounded-full bg-white/90 text-lg font-bold text-stone-700" data-testid={`hall-progress-${h.key}`}>
        {h.done || "完成"} {info?.completed ?? 0} / {info?.total ?? 0}
      </div>
    </div>
    <div className={`p-6 sm:p-8 flex-1 flex flex-col gap-2 ${h.font}`}>
      <div className={`text-3xl sm:text-4xl font-black whitespace-nowrap ${h.title}`}>{h.name}</div>
      <div className="text-xl text-stone-600">{h.sub}</div>
      <div className="text-lg text-stone-500">{h.learn}</div>
      <div className={`mt-auto pt-4 self-start kid-btn px-8 text-white text-2xl flex items-center gap-2 ${h.btn}`}>進去看看 <ChevronRight /></div>
    </div>
  </motion.button>
);

export default function Halls() {
  const nav = useNavigate();
  const childId = getChildId();
  const [child, setChild] = useState(null);
  const [info, setInfo] = useState(null);
  const [gate, setGate] = useState(false);
  useEffect(() => {
    if (!childId) return nav("/profiles");
    loadRecordings();
    api.get("/children").then(({ data }) => {
      const c = data.find((k) => k.child_id === childId);
      if (!c) return nav("/profiles");
      setChild(c);
      setAudioPrefs(c.settings);
    });
    api.get(`/halls?child_id=${childId}`).then(({ data }) => setInfo(data)).catch(() => nav("/profiles"));
  }, [childId, nav]);
  if (!child) return <div className="min-h-screen paper-bg" />;
  return (
    <div className="min-h-screen paper-bg flex flex-col" data-testid="halls-page">
      <header className="flex items-center justify-between px-4 sm:px-8 py-3">
        <button data-testid="halls-switch-child-btn" onClick={() => nav("/profiles")} className="flex items-center gap-3">
          <span className="text-4xl w-14 h-14 rounded-full bg-white border-4 border-amber-300 flex items-center justify-center overflow-hidden"><Avatar value={child.avatar} /></span>
          <span className="font-serif-tc text-2xl font-bold text-[var(--tea)]">{child.nickname}</span>
        </button>
        <div className="flex items-center gap-3">
          <button data-testid="halls-rewards-btn" onClick={() => nav("/rewards")} className="w-14 h-14 rounded-full bg-[var(--jade)] text-white flex items-center justify-center shadow-md"><Award size={28} /></button>
          <button data-testid="halls-parent-btn" onClick={() => setGate(true)} className="w-14 h-14 rounded-full bg-white border-2 border-stone-300 text-stone-500 flex items-center justify-center"><Users size={22} /></button>
        </div>
      </header>
      <main className="flex-1 max-w-6xl w-full mx-auto px-5 sm:px-8 pb-12">
        <motion.h1 initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="font-serif-tc font-black text-4xl sm:text-5xl lg:text-6xl text-[var(--tea)] mt-4 mb-8" data-testid="halls-title">
          今天想去哪一館？
        </motion.h1>
        <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
          {HALLS.map((h, i) => <HallCard key={h.key} h={h} i={i} info={info?.[h.key]} onGo={() => { play(h.name); nav(h.path); }} />)}
        </div>
      </main>
      <ParentGate open={gate} onOpenChange={setGate} onPass={() => nav("/parent")} />
    </div>
  );
}
